import React, { FunctionComponent, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { isEmpty, noop } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import {
  CostMessagingModalContent,
  CostMessagingModalCoverageInfo,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic/src/models/session/SessionUsage'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { ShieldTriageIcon } from '../../atoms/icons/ShieldTriageIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import { SecondaryButton } from '../../atoms/secondaryButton/SecondaryButton'
import { AuroraCareOption, AuroraCareOptionsMetadata } from '../../careOptionsMetadata/auroraCareOptionsMetadata'
import { useShouldShowCoverageMessaging } from '../../hooks/useShouldShowCoverageMessaging'
import { CareNavigatorCareOptionsFooter } from '../../molecules/careNavigatorCareOptionsFooter/CareNavigatorCareOptionsFooter'
import { CoverageMessagingModal } from '../../molecules/coverageMessagingModal/CoverageMessagingModal'
import { BodyTextSize } from '../../styles/typeStyles'
import { AuroraCareOptionCard } from '../../templates/auroraCareOptionCard/AuroraCareOptionCard'
import {
  CareOptionsGridCardCell,
  CareOptionsGridCell,
  CareOptionsGridSlotSize,
  getCareOptionsGridLayout,
  getOrderedGridCareOptions,
} from '../../utils/careOptions/gridLayout'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import {
  OnsiteProviderInfoModal,
  type OnsiteProviderInfoModalProps,
} from '../onsiteProviderInfoModal/OnsiteProviderInfoModal'
import { ProgramDetailsContent } from '../programDetailsModal/ProgramDetailsModal'

export interface CareOptionsGridProps {
  /** Care options to display. Ordered internally by the grid priority before laying out. */
  careOptions: AuroraCareOption[]
  onCareOptionPress: (careOption: AuroraCareOption) => void
  modalContentByOption?: Partial<Record<AuroraCareOption, ProgramDetailsContent>>
  formattedMessageValuesByOption?: Partial<Record<AuroraCareOption, Record<string, any>>>
  isLoading?: boolean
  onContactCareNavigatorPress?: () => void
  careNavigatorLinkTestId?: string
  careNavigatorFooterHeaderText?: React.ReactNode
  careNavigatorFooterEntryPointModal?: React.ReactNode
  showPrivacy?: boolean
  isCostMessagingEnabled?: boolean
  coverageModalTitleText?: React.ReactNode
  coverageModalContents?: CostMessagingModalContent[]
  costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo
  trackCoverageModalPress?: (isOpen: boolean) => void
  customerName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  onsiteModalProps?: Partial<OnsiteProviderInfoModalProps>
}

// Grid content max width (944px content area from the Aurora L+F MVP Figma).
const GRID_MAX_WIDTH = 944

// Desktop tertiary cards lay out on a 4-column grid (the hero and tall+stack rows span all four).
const TERTIARY_COLUMNS = 4

const SLOT_CELL_STYLES: Record<CareOptionsGridSlotSize, CSSObject> = {
  [CareOptionsGridSlotSize.HERO]: { width: '100%' },
  [CareOptionsGridSlotSize.CAROUSEL]: { width: '100%' },
  [CareOptionsGridSlotSize.MOBILE_FULL]: { width: '100%' },
  [CareOptionsGridSlotSize.SECONDARY_TALL]: { flexBasis: '464px', maxWidth: '464px', flexGrow: 0, flexShrink: 1 },
  [CareOptionsGridSlotSize.SECONDARY_SHORT]: { width: '100%' },
  [CareOptionsGridSlotSize.TERTIARY]: { flexBasis: '224px', maxWidth: '224px', flexGrow: 0, flexShrink: 1 },
  // Two-up on mobile: each small card takes half the row, so a pair fills the content width while a
  // lone trailing small card (odd count) stays half-width instead of stretching to full-width.
  [CareOptionsGridSlotSize.MOBILE_SMALL]: { flexBasis: '50%', flexGrow: 0, flexShrink: 1 },
}

const Content = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: '100%',
  maxWidth: theme.breakpoints.isMobileSized ? 'auto' : `${GRID_MAX_WIDTH}px`,
  marginHorizontal: 'auto',
  paddingHorizontal: theme.spacing['16px'],
  marginTop: theme.breakpoints.isMobileSized ? theme.spacing['24px'] : theme.spacing['32px'],
  marginBottom: theme.spacing['64px'],
}))

const GridRowView = styled.View<{ theme: ThemeType; isMobileSized: boolean }>(({ theme, isMobileSized }) => ({
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'stretch',
  gap: isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
  marginBottom: isMobileSized ? theme.spacing['8px'] : theme.spacing['16px'],
}))

const StackView = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing['16px'],
  flexBasis: '464px',
  maxWidth: '464px',
  flexGrow: 0,
  flexShrink: 1,
}))

const CardCellView = styled.View<{ cellStyle: CSSObject }>(({ cellStyle }) => ({
  ...cellStyle,
}))

const FooterContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['16px'],
}))

const PrivacyContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  marginTop: theme.spacing['24px'],
  alignItems: 'center',
  justifyContent: 'center',
}))

const StyledShieldIconContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['8px'],
}))

const HeroCtaContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  gap: theme.spacing['12px'],
}))

const LoadingContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingTop: theme.spacing['48px'],
  paddingBottom: theme.spacing['64px'],
}))

export const CareOptionsGrid: FunctionComponent<CareOptionsGridProps> = ({
  careOptions,
  onCareOptionPress,
  modalContentByOption = {},
  formattedMessageValuesByOption = {},
  isLoading = false,
  onContactCareNavigatorPress = noop,
  careNavigatorLinkTestId = 'CareOptionsGrid-ContactCareNavigatorLink',
  careNavigatorFooterHeaderText,
  careNavigatorFooterEntryPointModal,
  showPrivacy = true,
  isCostMessagingEnabled = false,
  coverageModalTitleText,
  coverageModalContents = [],
  costMessagingModalCoverageInfo,
  trackCoverageModalPress = noop,
  customerName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  onsiteModalProps,
}) => {
  const { colors, breakpoints } = useTheme() as ThemeType
  const { isMobileSized } = breakpoints
  const { formatMessage } = useIntl()
  const [isCoverageModalOpen, setIsCoverageModalOpen] = useState(false)

  const shouldShowCoverage = useShouldShowCoverageMessaging({
    isCostMessagingEnabled,
    costMessagingModalCoverageInfo,
    coverageModalContents,
  })

  const rows = useMemo(() => {
    const orderedOptions = getOrderedGridCareOptions(careOptions)
    return getCareOptionsGridLayout(orderedOptions, isMobileSized)
  }, [careOptions, isMobileSized])

  const renderHeroCtaSection = (careOption: AuroraCareOption): React.ReactNode => {
    if (careOption !== AuroraCareOption.SELF_TRIAGE || !shouldShowCoverage) {
      return undefined
    }
    const { buttonText } = AuroraCareOptionsMetadata[AuroraCareOption.SELF_TRIAGE]
    return (
      <HeroCtaContainer>
        <PrimaryButton
          text={buttonText ? <FormattedMessage {...buttonText} /> : undefined}
          onPress={() => onCareOptionPress(careOption)}
          testID={tID(`CareOptionsGrid-${careOption}-cta`)}
          fullWidth
        />
        <SecondaryButton
          text={formatMessage({
            defaultMessage: 'Understand your coverage',
            description: "Button to open a modal which explains the member's care coverage",
          })}
          onPress={() => {
            trackCoverageModalPress(true)
            setIsCoverageModalOpen(true)
          }}
          testID={tID('CareOptionsGrid-coverage')}
          fullWidth
        />
      </HeroCtaContainer>
    )
  }

  const renderCard = (cell: CareOptionsGridCardCell) => {
    const { careOption, size } = cell
    return (
      <CardCellView key={`${careOption}-${size}`} cellStyle={SLOT_CELL_STYLES[size]}>
        <AuroraCareOptionCard
          careOption={careOption}
          gridSlotSize={size}
          onPress={() => onCareOptionPress(careOption)}
          modalContent={modalContentByOption[careOption]}
          formattedMessageValues={formattedMessageValuesByOption[careOption]}
          heroCtaSection={size === CareOptionsGridSlotSize.HERO ? renderHeroCtaSection(careOption) : undefined}
        />
      </CardCellView>
    )
  }

  const renderCell = (cell: CareOptionsGridCell, index: number) => {
    if (cell.type === 'stack') {
      return <StackView key={`stack-${index}`}>{cell.cells.map(renderCard)}</StackView>
    }
    return renderCard(cell)
  }

  return (
    <Content testID={tID('CareOptionsGrid')}>
      {isLoading ? (
        <LoadingContainer>
          <LoadingIndicator topPadding={0} />
        </LoadingContainer>
      ) : (
        rows.map((row, rowIndex) => {
          const cells = row.cells.map(renderCell)
          // Pad a partial desktop tertiary row out to the full 4 columns with invisible fillers so it
          // shrinks identically to the tall+stack row above and its cards stay column-aligned when the
          // grid is narrower than its 944px design width.
          if (
            !isMobileSized &&
            row.cells.length < TERTIARY_COLUMNS &&
            row.cells.every((cell) => cell.type === 'card' && cell.size === CareOptionsGridSlotSize.TERTIARY)
          ) {
            for (let column = row.cells.length; column < TERTIARY_COLUMNS; column += 1) {
              cells.push(
                <CardCellView
                  key={`filler-${rowIndex}-${column}`}
                  cellStyle={SLOT_CELL_STYLES[CareOptionsGridSlotSize.TERTIARY]}
                />,
              )
            }
          }
          return (
            <GridRowView key={`row-${rowIndex}`} isMobileSized={isMobileSized}>
              {cells}
            </GridRowView>
          )
        })
      )}

      <FooterContainer>
        <CareNavigatorCareOptionsFooter
          careNavigatorLinkTestId={careNavigatorLinkTestId}
          onContactCareNavigatorPress={onContactCareNavigatorPress}
          headerText={careNavigatorFooterHeaderText}
        />
      </FooterContainer>

      {showPrivacy && (
        <PrivacyContainer>
          <StyledShieldIconContainer>
            <ShieldTriageIcon />
          </StyledShieldIconContainer>
          <BodyText
            text={
              <FormattedMessage
                defaultMessage='Responses are confidential'
                description='Description text explaining that responses are confidential'
              />
            }
            size={BodyTextSize.BADGE}
            textAlign='center'
            color={colors.textPrimary}
            bold
          />
        </PrivacyContainer>
      )}

      {careNavigatorFooterEntryPointModal}

      {shouldShowCoverage && (
        <CoverageMessagingModal
          isModalOpen={isCoverageModalOpen}
          setIsModalOpen={(isOpen) => {
            if (!isOpen) {
              trackCoverageModalPress(false)
            }
            setIsCoverageModalOpen(isOpen)
          }}
          coverageModalTitleText={coverageModalTitleText}
          coverageModalContents={coverageModalContents}
          costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
          trackCoverageModalPress={trackCoverageModalPress}
          customerName={customerName}
          numSessions={numSessions}
          numberOfSessionByProgram={numberOfSessionByProgram}
          displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
          displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
        />
      )}

      {!isEmpty(onsiteModalProps) ? <OnsiteProviderInfoModal {...onsiteModalProps} /> : null}
    </Content>
  )
}
